import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  ADDRESS_BUILDING,
  ADDRESS_CITY,
  ADDRESS_COORDINATES,
  ADDRESS_COUNTRY,
  ADDRESS_POST_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
} from 'constants/formFields';

import type { FieldValues, Path } from 'react-hook-form';
import type IAddressPanelProps from './interfaces/IAddressPanel';

import styles from './AddressPanel.module.scss';

function AddressPanel<T extends FieldValues>({
  control,
  placeholder,
  onFindCountries,
  onFindStates,
  onFindCities,
  isStatesDisabled,
  isCitiesDisabled,
  onChangeCountry,
  onChangeState,
  menuPosition,
  defaultCountries,
  requiredState,
}: IAddressPanelProps<T>) {
  const { t } = useTranslation();

  return (
    <DataSetsPanel title={t('suppliers.selections.address')}>
      <Controller
        control={control}
        name={ADDRESS_COUNTRY as Path<T>}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <Select
            name={name}
            label={t('suppliers.fields.country')}
            placeholder={placeholder || t('suppliers.fields.country')}
            value={value?.value ? value : null}
            onChange={(newValue) => {
              onChange(newValue);
              onChangeCountry();
            }}
            error={error?.message}
            loadOptions={onFindCountries}
            defaultOptions={defaultCountries}
            isSearchable
            isAsync
            isClearable
            className={styles.asyncSelect}
            menuPosition={menuPosition}
            required
          />
        )}
      />
      <Controller
        control={control}
        name={ADDRESS_STATE as Path<T>}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <Select
            name={name}
            label={t('suppliers.fields.state')}
            placeholder={placeholder || t('suppliers.fields.state')}
            value={value?.value ? value : null}
            onChange={(newValue) => {
              onChange(newValue);
              onChangeState();
            }}
            error={error?.message}
            loadOptions={onFindStates}
            isSearchable
            isAsync
            isClearable
            className={clsx(styles.asyncSelect, {
              [styles.inactiveSelect]: isStatesDisabled,
            })}
            isDisabled={isStatesDisabled}
            menuPosition={menuPosition}
            required={requiredState}
          />
        )}
      />
      <Controller
        control={control}
        name={ADDRESS_CITY as Path<T>}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <Select
            name={name}
            label={t('suppliers.fields.town')}
            placeholder={placeholder || t('suppliers.fields.town')}
            value={value?.value ? value : null}
            onChange={onChange}
            error={error?.message}
            loadOptions={onFindCities}
            isSearchable
            isAsync
            isClearable
            className={clsx(styles.asyncSelect, {
              [styles.inactiveSelect]: isCitiesDisabled || isStatesDisabled,
            })}
            isDisabled={isCitiesDisabled}
            menuPosition={menuPosition}
          />
        )}
      />
      <Controller
        control={control}
        name={ADDRESS_POST_CODE as Path<T>}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <TextField
            name={name}
            value={value}
            label={t('suppliers.fields.post')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder || t('suppliers.fields.post')}
          />
        )}
      />
      <Controller
        control={control}
        name={ADDRESS_STREET as Path<T>}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <TextField
            name={name}
            value={value}
            label={t('suppliers.fields.street')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder || t('suppliers.fields.street')}
          />
        )}
      />
      <Controller
        control={control}
        name={ADDRESS_BUILDING as Path<T>}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <TextField
            name={name}
            value={value}
            label={t('suppliers.fields.building')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder || t('suppliers.fields.building')}
          />
        )}
      />
      <Controller
        control={control}
        name={ADDRESS_COORDINATES as Path<T>}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <TextField
            name={name}
            value={value}
            label={t('suppliers.fields.coordinates')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder || t('suppliers.fields.coordinates')}
          />
        )}
      />
    </DataSetsPanel>
  );
}

export default AddressPanel;
